import { useEffect, useRef } from 'react';
import { RefreshCw, Users } from 'lucide-react';
import { reloadAfterMutation } from '../../lib/embedSafeReload';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';

interface SwapFourPlayersModalProps {
  show: boolean;
  mainPlayers: { id: number; name: string; isGoalie: boolean }[];
  reservePlayers: { id: number; name: string; isGoalie: boolean }[];
  onSwap: () => void;
  onCancel: () => void;
  isProcessing: boolean;
  language: 'en' | 'fr';
}

export function SwapFourPlayersModal({
  show,
  mainPlayers,
  reservePlayers,
  onSwap,
  onCancel,
  isProcessing,
  language
}: SwapFourPlayersModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();

  // Handler to refresh page after successful swap
  const handleSwap = async () => {
    try {
      const result: any = await onSwap();
      // If onSwap returns a result with success, only refresh if success is true or undefined (for backward compatibility)
      if (result === undefined || result?.success === undefined || result?.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
      // else do not refresh (validation failed)
    } catch (e) {
      // Do not refresh on error
    }
  };

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  if (!show) return null;

  const titleText = language === 'fr' ? 'Permutation de 4 Joueurs' : 'Swap 4 Players';
  const confirmText = language === 'fr' ? 'Confirmer la Permutation' : 'Confirm Swap';
  const cancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const processingText = language === 'fr' ? 'Traitement en cours...' : 'Processing...';
  
  const getConfirmationText = () => {
    return language === 'fr' 
      ? `Êtes-vous sûr de vouloir effectuer cette permutation entre le club principal et les réserves ?`
      : `Are you sure you want to perform this swap between the main club and reserves?`;
  };
  
  const noteText = language === 'fr'
    ? "Note: Cette action changera instantanément les statuts des 4 joueurs et comptera comme 2 rétrogradations."
    : "Note: This action will instantly change the status of all 4 players and count as 2 demotions.";

  return (
    <MyTeamsModal onClose={onCancel} busy={isProcessing}>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <Users size={20} className="me-2 text-primary" />
                {titleText}
              </h5>
              <button type="button" className="btn-close" onClick={onCancel}></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">{getConfirmationText()}</p>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">
                        <RefreshCw size={16} className="me-1" />
                        {language === 'fr' ? 'Club Principal → Réserves' : 'Main Club → Reserves'}
                      </h6>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-0">
                        {mainPlayers.map((player, index) => (
                          <li key={player.id} className="d-flex align-items-center mb-2">
                            <span className="badge bg-danger me-2">{index + 1}</span>
                            <span className="fw-medium">{player.name}</span>
                            {player.isGoalie && (
                              <span className="badge bg-info ms-2">
                                {language === 'fr' ? 'Gardien' : 'Goalie'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">
                        <RefreshCw size={16} className="me-1" />
                        {language === 'fr' ? 'Réserves → Club Principal' : 'Reserves → Main Club'}
                      </h6>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-0">
                        {reservePlayers.map((player, index) => (
                          <li key={player.id} className="d-flex align-items-center mb-2">
                            <span className="badge bg-success me-2">{index + 1}</span>
                            <span className="fw-medium">{player.name}</span>
                            {player.isGoalie && (
                              <span className="badge bg-info ms-2">
                                {language === 'fr' ? 'Gardien' : 'Goalie'}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="alert alert-warning mt-3">
                <small>
                  <strong>⚠️ {language === 'fr' ? 'Attention:' : 'Warning:'}</strong> {noteText}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isProcessing}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={handleSwap}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {processingText}
                  </>
                ) : (
                  <>
                    <Users size={16} className="me-2" />
                    {confirmText}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
      ></div>
    </MyTeamsModal>
  );
}